import { useState, useEffect, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import { useAuthContext } from "@mavenmm/teamwork-auth";
import { Tooltip, TooltipTrigger, TooltipContent } from "./ui/tooltip";
import type { ChatMessage } from "../types/assistant";

type Annotation = NonNullable<ChatMessage["annotations"]>[number];

type AssistantMarkdownProps = {
  content: string;
  annotations?: Annotation[];
  assistantType?: "spaces" | "projects" | "correspondence";
};

const HASURA_PROXY = "/.netlify/functions/hasura-proxy";

// TODO: verify production URL is correct
const PAAB_APP_URL = window.location.hostname === "localhost"
  ? "http://localhost:3000"
  : "https://paab.mavenmm.com";

const WEBPAGE_BY_FILE_ID_QUERY = `
query WebpageByOaiFileId($oaiFileId: String!) {
  paab_openai_webpage_files(where: { oai_file_id: { _eq: $oaiFileId } }, limit: 1) {
    url
    title
  }
}
`;

type WebpageLookup = { url: string; title: string } | null;

/**
 * Look up the actual webpage URL from the database using the OpenAI file_id
 */
function useWebpageUrls(annotations: Annotation[]) {
  const [urlMap, setUrlMap] = useState<Record<string, WebpageLookup>>({});
  const { getAccessToken } = useAuthContext();

  const fetchUrl = useCallback(async (fileId: string) => {
    try {
      const token = getAccessToken();
      if (!token) return null;
      const response = await fetch(HASURA_PROXY, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          query: WEBPAGE_BY_FILE_ID_QUERY,
          variables: { oaiFileId: fileId },
          operationName: "WebpageByOaiFileId",
        }),
      });
      const json = await response.json();
      const row = json.data?.paab_openai_webpage_files?.[0];
      return row ? { url: row.url, title: row.title } : null;
    } catch {
      return null;
    }
  }, [getAccessToken]);

  useEffect(() => {
    const fileIds = annotations
      .map((a) => a.file_id)
      .filter((id) => !(id in urlMap));

    if (fileIds.length === 0) return;

    Promise.all(
      fileIds.map(async (fileId) => {
        const result = await fetchUrl(fileId);
        return [fileId, result] as const;
      })
    ).then((results) => {
      setUrlMap((prev) => {
        const next = { ...prev };
        for (const [fileId, result] of results) {
          next[fileId] = result;
        }
        return next;
      });
    });
  }, [annotations, fetchUrl, urlMap]);

  return urlMap;
}

const PROJECT_FILE_BY_OAI_ID_QUERY = `
query ProjectFileByOaiId($fileId: String!) {
  paab_projects_files(
    where: {
      _or: [
        { copydeck_oai_file_id: { _eq: $fileId } },
        { paab_correspondence_oai_file_id: { _eq: $fileId } },
        { layout_oai_file_id: { _eq: $fileId } },
        { pushbacks: { oai_file_id: { _eq: $fileId } } }
      ]
    },
    limit: 1
  ) {
    teamwork_id
    copydeck_oai_file_id
    copydeck_s3_key
    paab_correspondence_oai_file_id
    paab_correspondence_s3_key
    layout_oai_file_id
    layout_s3_key
    pushbacks {
      oai_file_id
      pdf_s3_key
    }
  }
}
`;

type ProjectFileLookup = {
  filename: string;
  teamworkId: string | null;
  previewUrl: string;
} | null;

function useProjectFileUrls(
  annotations: Annotation[],
  assistantType?: string
) {
  const [fileMap, setFileMap] = useState<Record<string, ProjectFileLookup>>({});
  const { getAccessToken } = useAuthContext();

  const fetchProjectFile = useCallback(async (fileId: string) => {
    try {
      const token = getAccessToken();
      if (!token) return null;
      const response = await fetch(HASURA_PROXY, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          query: PROJECT_FILE_BY_OAI_ID_QUERY,
          variables: { fileId },
          operationName: "ProjectFileByOaiId",
        }),
      });
      const json = await response.json();
      const row = json.data?.paab_projects_files?.[0];
      if (!row) return null;

      // Determine which file matched and extract filename from S3 key
      let s3Key: string | null = null;
      if (row.copydeck_oai_file_id === fileId) {
        s3Key = row.copydeck_s3_key;
      } else if (row.paab_correspondence_oai_file_id === fileId) {
        s3Key = row.paab_correspondence_s3_key;
      } else if (row.layout_oai_file_id === fileId) {
        s3Key = row.layout_s3_key;
      } else {
        const pushback = row.pushbacks?.find((p: any) => p.oai_file_id === fileId);
        if (pushback) s3Key = pushback.pdf_s3_key;
      }

      const filename = s3Key ? s3Key.split("/").pop() || "Unknown file" : "Unknown file";
      const previewUrl = `${PAAB_APP_URL}/assistants?assistant=${assistantType || "projects"}&preview=${fileId}`;

      return {
        filename,
        teamworkId: row.teamwork_id,
        previewUrl,
      };
    } catch {
      return null;
    }
  }, [assistantType, getAccessToken]);

  useEffect(() => {
    const fileIds = annotations
      .map((a) => a.file_id)
      .filter((id) => !(id in fileMap));

    if (fileIds.length === 0) return;

    Promise.all(
      fileIds.map(async (fileId) => {
        const result = await fetchProjectFile(fileId);
        return [fileId, result] as const;
      })
    ).then((results) => {
      setFileMap((prev) => {
        const next = { ...prev };
        for (const [fileId, result] of results) {
          next[fileId] = result;
        }
        return next;
      });
    });
  }, [annotations, fetchProjectFile, fileMap]);

  return fileMap;
}

/**
 * Insert inline citation markers into the response text at annotation positions.
 * Maps each annotation's character index to a numbered reference like [1].
 */
function annotateText(
  text: string,
  annotations: Annotation[],
  fileIdToIndexMap: Map<string, number>
): string {
  // Build map: character index → citation marker
  const annotationMap = new Map<number, string>();
  for (const ann of annotations) {
    const num = fileIdToIndexMap.get(ann.file_id);
    if (num !== undefined && !annotationMap.has(ann.index)) {
      annotationMap.set(ann.index, `[**[${num}]**](#cite--${ann.file_id})`);
    }
  }

  if (annotationMap.size === 0) return text;

  // Insert markers at annotation positions (iterate backwards to preserve indices)
  const sortedIndices = [...annotationMap.keys()].sort((a, b) => b - a);
  let result = text;
  for (const idx of sortedIndices) {
    const marker = annotationMap.get(idx)!;
    result = result.slice(0, idx) + marker + result.slice(idx);
  }

  return result;
}

/**
 * Renders assistant message content with markdown formatting and citations
 */
export function AssistantMarkdown({ content, annotations, assistantType }: AssistantMarkdownProps) {
  const uniqueAnnotations = annotations ? dedupeAnnotations(annotations) : [];
  const isProjectType = assistantType === "projects" || assistantType === "correspondence";
  const urlMap = useWebpageUrls(isProjectType ? [] : uniqueAnnotations);
  const projectFileMap = useProjectFileUrls(
    isProjectType ? uniqueAnnotations : [],
    assistantType
  );

  // Build file_id → citation number map
  const fileIdToIndexMap = new Map<string, number>();
  for (const ann of uniqueAnnotations) {
    if (!fileIdToIndexMap.has(ann.file_id)) {
      fileIdToIndexMap.set(ann.file_id, fileIdToIndexMap.size + 1);
    }
  }

  // Insert inline citation markers into the text
  const annotatedContent = annotations && annotations.length > 0
    ? annotateText(content, annotations, fileIdToIndexMap)
    : content;

  return (
    <div className="assistant-markdown">
      <ReactMarkdown
        components={{
          a: ({ href, children, ...props }) => {
            // Inline citation links: resolve file_id to actual URL
            if (href?.startsWith("#cite--")) {
              const fileId = href.slice(7);

              if (isProjectType) {
                const projectFile = projectFileMap[fileId];
                const link = (
                  <a
                    href={projectFile?.previewUrl || `#cite-${fileIdToIndexMap.get(fileId) ?? ""}`}
                    target={projectFile?.previewUrl ? "_blank" : undefined}
                    rel={projectFile?.previewUrl ? "noopener noreferrer" : undefined}
                    className="text-blue-600 dark:text-maven-cyan-300 hover:underline cursor-pointer"
                    {...props}
                  >
                    {children}
                  </a>
                );
                if (projectFile?.filename) {
                  return (
                    <Tooltip>
                      <TooltipTrigger asChild>{link}</TooltipTrigger>
                      <TooltipContent>{projectFile.filename}</TooltipContent>
                    </Tooltip>
                  );
                }
                return link;
              }

              // Spaces logic
              const webpage = urlMap[fileId];
              const resolvedUrl = webpage?.url;
              const title = webpage?.title;
              const link = (
                <a
                  href={resolvedUrl || `#cite-${fileIdToIndexMap.get(fileId) ?? ""}`}
                  target={resolvedUrl ? "_blank" : undefined}
                  rel={resolvedUrl ? "noopener noreferrer" : undefined}
                  className="text-blue-600 dark:text-maven-cyan-300 hover:underline cursor-pointer"
                  {...props}
                >
                  {children}
                </a>
              );
              if (title) {
                return (
                  <Tooltip>
                    <TooltipTrigger asChild>{link}</TooltipTrigger>
                    <TooltipContent>{title}</TooltipContent>
                  </Tooltip>
                );
              }
              return link;
            }
            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 dark:text-maven-cyan-300 hover:underline"
                {...props}
              >
                {children}
              </a>
            );
          },
          ul: ({ children }) => (
            <ul className="list-disc ml-4 my-2">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal ml-4 my-2">{children}</ol>
          ),
          li: ({ children }) => <li className="my-1">{children}</li>,
          p: ({ children }) => <p className="my-2 first:mt-0 last:mb-0">{children}</p>,
          code: ({ children, className }) => {
            const isInline = !className;
            return isInline ? (
              <code className="bg-gray-200 px-1 py-0.5 rounded text-sm font-mono">
                {children}
              </code>
            ) : (
              <code className="block bg-gray-800 text-gray-100 p-3 rounded my-2 text-sm font-mono overflow-x-auto">
                {children}
              </code>
            );
          },
          pre: ({ children }) => <pre className="my-2">{children}</pre>,
          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
          h1: ({ children }) => <h1 className="text-lg font-bold mt-4 mb-2">{children}</h1>,
          h2: ({ children }) => <h2 className="text-base font-bold mt-3 mb-2">{children}</h2>,
          h3: ({ children }) => <h3 className="text-sm font-bold mt-2 mb-1">{children}</h3>,
        }}
      >
        {annotatedContent}
      </ReactMarkdown>

      {/* Sources list */}
      {uniqueAnnotations.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-600">
          <div className="font-medium mb-2">Sources:</div>
          <ul className="space-y-1">
            {uniqueAnnotations.map((annotation, idx) => {
              if (isProjectType) {
                const pf = projectFileMap[annotation.file_id];
                return (
                  <li key={annotation.file_id} id={`cite-${idx + 1}`} className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-maven-cyan-300 shrink-0">[{idx + 1}]</span>
                    {pf?.previewUrl ? (
                      <a href={pf.previewUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-maven-cyan-300 hover:underline break-words">
                        {pf.filename}
                      </a>
                    ) : (
                      <span className="break-words">{annotation.filename}</span>
                    )}
                    {pf?.teamworkId && (
                      <a
                        href={`https://mavenmm.teamwork.com/app/projects/${pf.teamworkId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-gray-600 shrink-0"
                      >
                        (Teamwork)
                      </a>
                    )}
                  </li>
                );
              }

              const webpage = urlMap[annotation.file_id];
              let url = webpage?.url;
              const title = webpage?.title;

              // Deep link to timestamp for Vimeo video citations
              if (url && isVimeoPlayerUrl(url) && annotation.search_result_text) {
                const seconds = extractVimeoTimestamp(annotation.search_result_text, content, annotation.index);
                if (seconds !== null) {
                  url = `${url}#t=${seconds}s`;
                }
              }

              return (
                <li key={annotation.file_id} id={`cite-${idx + 1}`} className="flex items-start gap-2">
                  <span className="text-blue-600 dark:text-maven-cyan-300 shrink-0">[{idx + 1}]</span>
                  {url ? (
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-maven-cyan-300 hover:underline break-words"
                    >
                      {title || url}
                    </a>
                  ) : (
                    <span className="break-words">{annotation.filename}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Extract the [M:SS] timestamp from search result text closest to the content
 * the model actually cited. Uses words from the response text near the citation
 * to locate the relevant position in the chunk, then picks the nearest preceding timestamp.
 * Falls back to the middle timestamp if no match is found.
 */
function extractVimeoTimestamp(searchText: string, responseText?: string, citationIndex?: number): number | null {
  const matches = [...searchText.matchAll(/\[(\d+):(\d{2})\]/g)];
  if (matches.length === 0) return null;

  // Try to find the cited content in the search chunk using co-located word pairs.
  // Only use the result if we find a pair match (high confidence). Otherwise fall back
  // to middle timestamp which is a safer approximation than a single-word match.
  if (responseText && citationIndex !== undefined) {
    const start = Math.max(0, citationIndex - 80);
    const snippet = responseText.slice(start, citationIndex).toLowerCase();
    const words = snippet.match(/\b[a-z]{5,}\b/g);
    if (words && words.length >= 2) {
      const chunkLower = searchText.toLowerCase();

      // Search for co-located word pairs (both words within 80 chars)
      for (let i = words.length - 2; i >= 0; i--) {
        const pos = chunkLower.indexOf(words[i]);
        if (pos === -1) continue;
        const region = chunkLower.slice(pos, pos + 80);
        if (region.includes(words[i + 1])) {
          let closest = matches[0];
          for (const m of matches) {
            if (m.index !== undefined && m.index <= pos) {
              closest = m;
            }
          }
          return parseInt(closest[1]) * 60 + parseInt(closest[2]);
        }
      }
    }
  }

  // Fallback: middle timestamp
  const mid = matches[Math.floor(matches.length / 2)];
  return parseInt(mid[1]) * 60 + parseInt(mid[2]);
}

function isVimeoPlayerUrl(url: string): boolean {
  return url.includes("player.vimeo.com/video/");
}

/**
 * Deduplicate annotations by file_id, keeping first occurrence
 */
function dedupeAnnotations(annotations: Annotation[]): Annotation[] {
  const seen = new Set<string>();
  return annotations.filter((a) => {
    if (seen.has(a.file_id)) return false;
    seen.add(a.file_id);
    return true;
  });
}
